import classnames from 'classnames';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { Children, cloneElement, isValidElement, ReactNode } from 'react';
import { shallowEqual } from 'react-redux';

import { useBreakpointValue } from 'utils/mediaQuery/useBreakpointValue';
import { pubSub } from 'utils/pubSub';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { SidekickTasksProvider } from 'utils/sidekick';
import { SidekickStartersProvider } from 'utils/sidekickStarters';

import { Sidekick } from '../sidekick/sidekick';

import { ClientRefreshBanner } from './components/clientRefreshBanner/clientRefreshBanner';
import { Navigation } from './components/navigation/navigation';
import { TopBar } from './components/topBar/topBar';
import styles from './layoutContainer.module.scss';

/** Represents the props of the layout container. */
type LayoutContainerProps = PropsWithChildren;

/**
 * Represents the component where the layout of the agent interface and all its
 * routed views are rendered.
 */
export const LayoutContainer = ({
  children,
}: LayoutContainerProps): JSX.Element => {
  const { presence, profile, isFocusMode } = useAppSelector(
    (state) => ({
      presence: state.presence,
      profile: state.settings.profile.profile,
      isFocusMode: state.conversation.isFocusMode,
    }),
    shallowEqual,
  );

  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  const handleToggleNavigation = () => {
    pubSub.publish('toggleNavigation', isSmallScreen ? {} : { isOpen: false });
  };

  return (
    <div
      className={classnames({
        [styles.root]: true,
        [styles.rootFocusMode]: isFocusMode,
      })}
    >
      <ClientRefreshBanner className={styles.banner} />

      <TopBar onToggleNavigation={handleToggleNavigation} />

      <Navigation className={classnames(styles.nav, 'print-hidden')} />

      <SidekickStartersProvider>
        <SidekickTasksProvider>
          <main className={styles.main}>
            <div className={styles.content} id="content">
              {Children.map<ReactNode, ReactNode>(children, (child) => {
                return isValidElement(child)
                  ? cloneElement(child, { profile, presence } as never)
                  : undefined;
              })}
            </div>

            <Sidekick className={classnames(styles.aside, 'print-hidden')} />
          </main>
        </SidekickTasksProvider>
      </SidekickStartersProvider>
    </div>
  );
};
